import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { PageHero, Section, DynamicIcon, EmptyState } from "@/components/site/bits";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/propostas")({
  head: () => ({
    meta: [
      { title: "Propostas | Professor Rurdiney — 40123" },
      {
        name: "description",
        content:
          "Conheça as propostas do Professor Rurdiney para educação, saúde, cultura e mais em São Luís. Vote 40123.",
      },
      { property: "og:title", content: "Propostas | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Propostas concretas para transformar São Luís. Confira o plano de governo.",
      },
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(listQuery("proposals")),
      context.queryClient.ensureQueryData(listQuery("proposal_categories")),
    ]),
  component: PropostasPage,
});

function PropostasPage() {
  const { data: proposals } = useSuspenseQuery(listQuery("proposals"));
  const { data: categories } = useSuspenseQuery(listQuery("proposal_categories"));
  const [active, setActive] = useState<string | null>(null);

  const filtered = active
    ? proposals.filter((p: any) => p.category_id === active)
    : proposals;

  return (
    <>
      <PageHero
        eyebrow="Plano de governo"
        title="Propostas"
        description="Ideias concretas, construídas com a comunidade, para uma São Luís mais justa, educada e inclusiva."
      />

      <Section>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActive(null)}
            className={cn(
              "rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
              active === null
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary",
            )}
          >
            Todas
          </button>
          {categories.map((c: any) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors",
                active === c.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary",
              )}
            >
              <DynamicIcon name={c.icon} className="size-4" />
              {c.name}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10">
            <EmptyState message="Nenhuma proposta encontrada para este filtro." />
          </div>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p: any) => (
              <Link
                key={p.id}
                to="/propostas/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-all hover:border-primary hover:shadow-glow"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <DynamicIcon name={p.icon} className="size-6" />
                </div>
                <h3 className="mt-4 text-xl text-foreground">{p.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {p.summary}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-primary group-hover:text-accent-deep">
                  Saiba mais <ArrowRight className="size-4" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}
